<template>
  <slot name="actions" v-bind="{ showFeatureLists, onComparePlans }">
    <AppButton
      v-if="!showFeatureLists"
      class="fl-ComparePlansButton"
      icon="arrow-down-simple-next"
      type="link-next"
      @click="onComparePlans"
    >
      {{ t('subscription.compare_plans') }}
    </AppButton>
  </slot>

  <transition mode="out-in" name="fade">
    <div v-if="showFeatureLists" class="fl-FeatureLists">
      <div class="fl-FeatureLists_Wrapper">
        <div class="fl-FeatureList">
          <div class="fl-FeatureItem_Title"></div>
          <div v-for="list in FEATURE_LIST" :key="list.name" class="fl-FeatureItem">
            <div class="fl-FeatureItem_Label">{{ list.name }}</div>
          </div>
        </div>
        <div v-for="list in getFeatureListByPlan" :key="list.name" class="fl-FeatureList">
          <div class="fl-FeatureItem_Title">{{ t(`subscription.${list.name}`) }}</div>
          <div
            v-for="(feature, featureIndex) in list.features"
            :key="featureIndex"
            class="fl-FeatureItem"
          >
            <div class="fl-FeatureItem_Label">
              <span v-if="feature.text">{{ feature.text }} </span>
              <AppIcon
                v-else-if="feature.include"
                height="24"
                icon-name="check-active"
                width="24"
              />
              <AppIcon v-else height="24" icon-name="clear" width="24" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
defineOptions({
  name: 'FeatureList'
})

import { inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES } from '@/tracking/amplitude-helpers'
import { isAzureAppInjectionKey } from '@/utils/injection-keys'
import { LIMITATIONS, RESTRICTIONS_IDS } from '@/utils/web-app/plans-limitations'

import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'

const showFeatureLists = ref(false)

const { t } = useI18n()

const isAzureApp = inject(isAzureAppInjectionKey)

const ESSENTIAL = 'essential'
const PROFESSIONAL = 'professional'
const ENTERPRISE = 'enterprise'

const PLAN_LIST = [ESSENTIAL, PROFESSIONAL, ENTERPRISE]

/**
 * A single row of the plans comparison grid.
 *
 * @typedef {Object} PlanFeature
 * @property {string} name - Row label, also used as the row key in `getFeatureListByPlan`.
 * @property {Record<'essential' | 'professional' | 'enterprise', PlanCell>} plans - One cell per plan.
 */

/**
 * A single cell of the grid. `include` renders a check/cross icon, `text` renders a label.
 *
 * @typedef {{ include?: boolean } | { text?: string }} PlanCell
 */

/**
 * Builds a comparison row, one cell per plan.
 *
 * @param {'include' | 'text'} valueKey - Which cell shape to produce.
 * @param {string} name - Row label.
 * @param {boolean | string} essential - Value for the Essential column.
 * @param {boolean | string} professional - Value for the Professional column.
 * @param {boolean | string} enterprise - Value for the Enterprise column.
 * @returns {PlanFeature}
 */
const createFeature = (valueKey, name, essential, professional, enterprise) => {
  return {
    name,
    plans: {
      [ESSENTIAL]: { [valueKey]: essential },
      [PROFESSIONAL]: { [valueKey]: professional },
      [ENTERPRISE]: { [valueKey]: enterprise }
    }
  }
}

/**
 * A row where every plan shows a check or a cross icon.
 *
 * @param {string} name - Row label.
 * @param {boolean} essential - Included in Essential.
 * @param {boolean} professional - Included in Professional.
 * @param {boolean} enterprise - Included in Enterprise.
 * @returns {PlanFeature}
 * @example includeFeature('Weighting', false, true, true)
 */
const includeFeature = (name, essential, professional, enterprise) => {
  return createFeature('include', name, essential, professional, enterprise)
}

/**
 * A row where every plan shows a text label instead of an icon.
 *
 * @param {string} name - Row label.
 * @param {string} essential - Label for Essential, e.g. `Up to 3`.
 * @param {string} professional - Label for Professional.
 * @param {string} enterprise - Label for Enterprise.
 * @returns {PlanFeature}
 * @example textFeature('Workspaces', 'Up to 3', 'Up to 10', 'Unlimited')
 */
const textFeature = (name, essential, professional, enterprise) => {
  return createFeature('text', name, essential, professional, enterprise)
}

/**
 * Drops a row from the comparison grid entirely — it disappears instead of
 * rendering a cross. Nulls are stripped by the `.filter(Boolean)` on FEATURE_LIST.
 *
 * @param {boolean} condition - Hide the row when true. Combine platforms with `||`.
 * @param {PlanFeature} feature - Row to keep or drop.
 * @returns {PlanFeature | null}
 * @example hideIf(isAzureApp || isJiraApp, includeFeature('SSO', false, false, true))
 */
const hideIf = (condition, feature) => (condition ? null : feature)

const FEATURE_LIST = [
  includeFeature('OKRs & KPIs management', true, true, true),
  includeFeature('Check-ins (reminders, notifications)', true, true, true),
  textFeature(
    'Workspaces',
    `Up to ${LIMITATIONS.WORKSPACES[RESTRICTIONS_IDS.ID_1]}`,
    `Up to ${LIMITATIONS.WORKSPACES[RESTRICTIONS_IDS.ID_2]}`,
    'Unlimited'
  ),
  textFeature(
    'Custom strategic components',
    `Up to ${LIMITATIONS.OKR_LEVELS[RESTRICTIONS_IDS.ID_1]}`,
    `Up to ${LIMITATIONS.OKR_LEVELS[RESTRICTIONS_IDS.ID_2]}`,
    'Unlimited'
  ),
  textFeature(
    'Custom dashboard public',
    `Up to ${LIMITATIONS.CUSTOM_DASHBOARD_PUBLIC[RESTRICTIONS_IDS.ID_1]}`,
    `Up to ${LIMITATIONS.CUSTOM_DASHBOARD_PUBLIC[RESTRICTIONS_IDS.ID_2]}`,
    'Unlimited'
  ),
  textFeature(
    'Custom fields',
    `Up to ${LIMITATIONS.CUSTOM_FIELDS[RESTRICTIONS_IDS.ID_1]}`,
    `Up to ${LIMITATIONS.CUSTOM_FIELDS[RESTRICTIONS_IDS.ID_2]}`,
    `Up to ${LIMITATIONS.CUSTOM_FIELDS[RESTRICTIONS_IDS.ID_3]}`
  ),
  hideIf(isAzureApp, includeFeature('Oboard MCP', true, true, true)),
  hideIf(isAzureApp, includeFeature('Oboard AI skills', true, true, true)),
  includeFeature('Hierarchical business groups', true, true, true),
  includeFeature('Public and private workspaces', true, true, true),
  includeFeature('Public API', true, true, true),
  includeFeature('Excel / PDF export', true, true, true),
  includeFeature('OKR Map', true, true, true),
  includeFeature('Roadmap', true, true, true),
  includeFeature('Deeper OKR hierarchy', true, true, true),
  includeFeature('Custom reporting', true, true, true),
  includeFeature('Notifications', true, true, true),
  includeFeature('Jira integration', true, true, true),
  includeFeature('Private OKRs', true, true, true),
  includeFeature('Custom thresholds', true, true, true),
  includeFeature('Custom metric types', true, true, true),
  includeFeature('Weighting', false, true, true),
  includeFeature('Audit log (user, future)', false, true, true),
  includeFeature('Account manager', false, false, true),
  includeFeature('Power BI connector', false, false, true),
  includeFeature('Priority support', false, false, true),
  includeFeature('Consulting discount', false, false, true),
  hideIf(isAzureApp, includeFeature('SSO', false, false, true))
].filter(Boolean)

const getFeatureListByPlan = PLAN_LIST.map(plan => {
  return {
    name: plan,
    features: FEATURE_LIST.reduce((acc, feature) => {
      acc = {
        ...acc,
        [feature.name]: {
          ...feature.plans[plan]
        }
      }
      return acc
    }, {})
  }
})

const onComparePlans = () => {
  showFeatureLists.value = !showFeatureLists.value
  tracker.logEvent('plans comparison show', {
    category: EVENT_CATEGORIES.BILLING
  })
}
</script>

<style lang="scss" scoped>
.fl-ComparePlansButton {
  margin: 0 auto;
  font-weight: fw('semi-bold');
}
.fl-FeatureLists {
  &_Wrapper {
    display: grid;
    gap: 32px;
    grid-template-columns: 310px 1fr 1fr 1fr;
    margin: -44px auto auto auto;
  }
  &:before {
    display: block;
    content: '';
    width: 100%;
    height: 40px;
    position: sticky;
    top: calc(var(--ob-fl-feature-list-header-title-top, 0px) + 44px);
    background: var(
      --ob-fl-feature-list-header-title-background-color,
      linear-gradient(
        180deg,
        rgba(255, 255, 255, 1) 15%,
        rgba(255, 255, 255, 0.4) 80%,
        rgba(167, 105, 246, 0) 100%
      )
    );
  }
}
.fl-FeatureList {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.fl-FeatureItem_Label {
  display: flex;
  color: $dark-2;
  font-size: $fs-14;
  font-style: normal;
  font-weight: fw('semi-bold');
  line-height: 24px;
  border-bottom: 1px solid $grey-2-next;
  padding: 0 32px 8px 32px;
}
.fl-FeatureItem_Title {
  color: $dark-1;
  font-size: $fs-20;
  font-weight: fw('semi-bold');
  line-height: 28px;
  border-bottom: 2px solid $grey-2-next;
  padding: 0 32px 16px 32px;
  height: 44px;
  position: sticky;
  top: var(--ob-fl-feature-list-header-title-top, 0);
  background-color: var(--ob-fl-feature-list-header-title-background-color, $white);
}
</style>
